/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectEmbedToken, selectTabList } from 'modules/dashboard/selectors';
import {
  selectIsCumulative,
  selectTrendTimeWeeklyView,
  selectRevenueIsDisplayTH,
  selectTrendHotelFocusOn,
  selectTrendHotelNormalizedView,
  selectTrendHotelTabularVisuals,
  selectTrendHotelWeeklyTabularVisuals,
  selectTrendHotelCumulativeTabularVisuals,
  selectTrendHotelPercentageTabularVisuals,
  selectIsPercentageChange,
  selectTrendHotelTabularFilterList,
  selectActiveTrendTab,
  selectIsMovingAverage,
  selectMovingAverageTabularVisualTH,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import VisualGrid from 'modules/dashboard/components/tab-container/visual-grid';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import ExportToCSV from 'modules/dashboard/components/tab-container/export-visual-data/export-csv';
import { REPORT_TYPE } from 'modules/dashboard/constants';
import { isEmpty } from '../../../../../../../../common/helpers/object';
/**
 * Trend Hotel tabular report with filters
 */
const TrendHotelTabularWidget = ({ setIsHotelLoaded }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const tabularFilterList = useSelector(selectTrendHotelTabularFilterList);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const isCumulative = useSelector(selectIsCumulative);
  const visuals = useSelector(selectTrendHotelTabularVisuals);
  const weeklyVisuals = useSelector(selectTrendHotelWeeklyTabularVisuals);
  const cumulativeVisuals = useSelector(selectTrendHotelCumulativeTabularVisuals);
  const tokenDetails = useSelector(selectEmbedToken);
  const revenueIsDisplay = useSelector(selectRevenueIsDisplayTH);
  const focusOn = useSelector(selectTrendHotelFocusOn);
  const normalizedViewToggle = useSelector(selectTrendHotelNormalizedView);
  const percentageVisuals = useSelector(selectTrendHotelPercentageTabularVisuals);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const activeTab = useSelector(selectActiveTrendTab);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const movingAverageHotelTabularVisuals = useSelector(selectMovingAverageTabularVisualTH);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [tabularCombined, setTabularCombined] = useState({});
  const [isTabularCombinedRendered, setIsTabularCombinedRendered] = useState({});
  // To get visual details
  const getVisual = (index) => {
    let found;
    if (isCumulative) {
      found = cumulativeVisuals?.find((visual) => visual?.order === index);
    } else if (weeklyViewToggle) {
      found = weeklyVisuals?.find((visual) => visual?.order === index);
    } else if (isPercentageChange && !weeklyViewToggle && !isCumulative && !normalizedViewToggle) {
      found = percentageVisuals?.find((visual) => visual?.order === index);
    } else if (isMovingAverage && !isPercentageChange && !weeklyViewToggle) {
      found = movingAverageHotelTabularVisuals?.find((visual) => visual?.order === index);
    } else {
      found = visuals?.find((visual) => visual?.order === index);
    }
    return found;
  };
  // To get PBI visual instance based on the configuration
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    setIsRendered = () => {},
    displayFunction = () => {},
    filter = tabularFilterList
  ) => {
    const found = getVisual(index);
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={tokenDetails?.accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filter}
          setPBIReport={(r) => setPBIReport(r)}
          setIsLoaded={(r) => setIsLoaded(r)}
          setIsRendered={(r) => setIsRendered(r)}
          tab={TAB_VALUE.PBI_TREND}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  // To get visual names based on visual order
  const getVisualName = (order) => getVisual(order)?.name;
  //
  const trendFilters = `Compared Hotels: , "${focusOn?.label}"\r\n`;
  //
  return (
    !isEmpty(tabularFilterList) && (
      <VisualGrid
        isDisplay={revenueIsDisplay}
        visualName={getVisualName(0)}
        visual={getWidget(
          0,
          styles.tabularVisual,
          setTabularCombined,
          setIsHotelLoaded,
          setIsTabularCombinedRendered,
          trendActions.setRevenueIsDisplayTH,
          tabularFilterList
        )}
        onClick={() => {
          dispatch(trendActions.setRevenueIsDisplayTH(!revenueIsDisplay));
        }}
        toggleFragment={
          <ExportToCSV
            exportExcel
            report={tabularCombined}
            isLoaded={isTabularCombinedRendered && revenueIsDisplay}
            fileName={
              isPercentageChange
                ? 'TVH_Percentage'
                : isMovingAverage
                ? 'CMATH_Daily'
                : isCumulative
                ? 'TVH_Cumulative'
                : weeklyViewToggle
                ? 'TVH_Weekly'
                : 'TVH_Daily'
            }
            weeklyViewToggle={weeklyViewToggle}
            isCumulative={isCumulative}
            otherFilters={trendFilters}
          />
        }
        isWorkbookHotelComparison={activeTab === REPORT_TYPE.TREND_HOTEL}
        comparisonHotel={focusOn}
        displayHotelName
      />
    )
  );
};
//
export default TrendHotelTabularWidget;
